import React, { createContext, useContext, useReducer, useEffect, ReactNode } from 'react';
import AsyncStorage from '@react-native-async-storage/async-storage';
import { useAuth } from './AuthContext';

interface StoreType {
  id: string;
  name: string;
  description: string;
  size: string;
  capacity: string;
  initialCost: number;
  monthlyRent: number;
  features: string[];
  image: string;
}

interface GameState {
  // Game progress
  currentStep: number;
  currentDay: number;
  gameTime: string;
  isPlaying: boolean;
  gameSpeed: number;
  
  // Player stats
  experience: number;
  level: number;
  reputation: number;
  
  // Currency
  turkishLira: number;
  niceLira: number;
  nicePoint: number;
  
  // Store info
  storeName: string;
  storeType: StoreType | string;
  location: string;
  character: string;
  username: string;
  
  // Game data
  customers: any[];
  menu: any[];
  equipment: any[];
  quests: any[];
  achievements: any[];
  notifications: any[];
  statistics: any;
  
  // Loading states
  isLoading: boolean;
  error: string | null;
}

type GameAction =
  | { type: 'GAME_LOADING' }
  | { type: 'GAME_LOADED'; payload: Partial<GameState> }
  | { type: 'GAME_ERROR'; payload: string }
  | { type: 'UPDATE_GAME_STATE'; payload: Partial<GameState> }
  | { type: 'SAVE_GAME_SUCCESS' }
  | { type: 'RESET_GAME' };

interface GameContextType extends GameState {
  updateGameState: (updates: Partial<GameState>) => void;
  saveGameData: () => Promise<void>;
  loadGameData: () => Promise<void>;
  resetGame: () => void;
  addNotification: (notification: any) => void;
  updateQuest: (questId: string, progress: any) => void;
  addAchievement: (achievement: any) => void;
}

const GameContext = createContext<GameContextType | undefined>(undefined);

const gameReducer = (state: GameState, action: GameAction): GameState => {
  switch (action.type) {
    case 'GAME_LOADING':
      return {
        ...state,
        isLoading: true,
        error: null
      };
    case 'GAME_LOADED':
      return {
        ...state,
        ...action.payload,
        isLoading: false,
        error: null
      };
    case 'GAME_ERROR':
      return {
        ...state,
        isLoading: false,
        error: action.payload
      };
    case 'UPDATE_GAME_STATE':
      return {
        ...state,
        ...action.payload
      };
    case 'SAVE_GAME_SUCCESS':
      return {
        ...state,
        error: null
      };
    case 'RESET_GAME':
      return {
        ...initialGameState,
        isLoading: false,
        error: null
      };
    default:
      return state;
  }
};

const initialGameState: GameState = {
  currentStep: 0,
  currentDay: 1,
  gameTime: '08:00',
  isPlaying: false,
  gameSpeed: 1,
  experience: 0,
  level: 1,
  reputation: 50,
  turkishLira: 400000,
  niceLira: 400000,
  nicePoint: 1000,
  storeName: '',
  storeType: '',
  location: '',
  character: '',
  username: '',
  customers: [],
  menu: [],
  equipment: [],
  quests: [],
  achievements: [],
  notifications: [],
  statistics: {
    totalCustomers: 0,
    totalRevenue: 0,
    totalOrders: 0,
    averageSatisfaction: 0,
    manualPreparations: 0,
    automaticPreparations: 0
  },
  isLoading: false,
  error: null
};

export function GameProvider({ children }: { children: ReactNode }) {
  const [state, dispatch] = useReducer(gameReducer, initialGameState);
  const { user, isAuthenticated } = useAuth();

  // Load game data when component mounts
  useEffect(() => {
    loadGameData();
  }, []);

  // Auto-save game data every 30 seconds when playing
  useEffect(() => {
    if (isAuthenticated && user && state.isPlaying) {
      const interval = setInterval(() => {
        saveGameData();
      }, 30000); // 30 seconds

      return () => clearInterval(interval);
    }
  }, [isAuthenticated, user, state.isPlaying]);

  // Auto-save when game state changes (for authenticated users)
  useEffect(() => {
    if (isAuthenticated && user && state.currentStep > 0) {
      const timeoutId = setTimeout(() => {
        saveGameData();
      }, 2000); // Save after 2 seconds of inactivity

      return () => clearTimeout(timeoutId);
    }
  }, [isAuthenticated, user, state.currentStep, state.currentDay, state.turkishLira, state.niceLira, state.nicePoint, state.experience, state.level, state.reputation]);

  const loadGameData = async () => {
    try {
      dispatch({ type: 'GAME_LOADING' });

      // Load from AsyncStorage
      const gameDataKey = user ? `game_data_${user.id || user.email}` : 'game_data_guest';
      const savedGameData = await AsyncStorage.getItem(gameDataKey);
      
      if (savedGameData) {
        const gameData = JSON.parse(savedGameData);
        // Preserve the saved game data completely, only update username if missing
        dispatch({
          type: 'GAME_LOADED',
          payload: {
            ...gameData,
            username: gameData.username || gameData.storeName || (user?.firstName) || 'Oyuncu'
          }
        });
      } else {
        // No existing game data, create new
        const newGameData = {
          ...initialGameState,
          username: (user?.firstName) || 'Oyuncu'
        };
        await AsyncStorage.setItem(gameDataKey, JSON.stringify(newGameData));
        dispatch({ 
          type: 'GAME_LOADED', 
          payload: newGameData
        });
      }
    } catch (error: any) {
      console.error('Load game data error:', error);
      dispatch({ type: 'GAME_ERROR', payload: 'Oyun verileri yüklenirken hata oluştu' });
    }
  };

  const saveGameData = async () => {
    try {
      const gameDataKey = user ? `game_data_${user.id || user.email}` : 'game_data_guest';
      await AsyncStorage.setItem(gameDataKey, JSON.stringify(state));
      dispatch({ type: 'SAVE_GAME_SUCCESS' });
    } catch (error: any) {
      console.error('Save game data error:', error);
      dispatch({ type: 'GAME_ERROR', payload: 'Oyun verileri kaydedilirken hata oluştu' });
    }
  };

  const updateGameState = (updates: Partial<GameState>) => {
    dispatch({ type: 'UPDATE_GAME_STATE', payload: updates });
  };

  const resetGame = async () => {
    try {
      // Clear all AsyncStorage game data
      await AsyncStorage.removeItem('game_data_guest');
      if (user) {
        await AsyncStorage.removeItem(`game_data_${user.id || user.email}`);
      }
    } catch (error) {
      console.error('Error clearing game data:', error);
    }
    
    // Reset to initial state
    dispatch({ type: 'RESET_GAME' });
  };

  const addNotification = (notification: any) => {
    const newNotification = {
      id: Date.now().toString(),
      timestamp: new Date().toISOString(),
      read: false,
      priority: 'normal',
      ...notification
    };

    updateGameState({
      notifications: [...state.notifications, newNotification]
    });

    // Auto-save game state when notification is added
    if (user) {
      setTimeout(() => {
        saveGameData();
      }, 1000);
    }
  };

  const updateQuest = (questId: string, progress: any) => {
    const updatedQuests = state.quests.map(quest =>
      quest.id === questId ? { ...quest, ...progress } : quest
    );
    updateGameState({ quests: updatedQuests });
  };

  const addAchievement = (achievement: any) => {
    const newAchievement = {
      id: Date.now().toString(),
      unlockedAt: new Date().toISOString(),
      ...achievement
    };

    updateGameState({
      achievements: [...state.achievements, newAchievement]
    });

    addNotification({
      type: 'achievement',
      title: 'Başarım Kazanıldı!',
      message: `${achievement.title} başarımını kazandınız!`,
      priority: 'high'
    });
  };

  const value: GameContextType = {
    ...state,
    updateGameState,
    saveGameData,
    loadGameData,
    resetGame,
    addNotification,
    updateQuest,
    addAchievement
  };

  return (
    <GameContext.Provider value={value}>
      {children}
    </GameContext.Provider>
  );
}

export function useGame() {
  const context = useContext(GameContext);
  if (context === undefined) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
}